import { Schema } from 'prosemirror-model';
import { addListNodes } from 'prosemirror-schema-list';
import { tableNodes } from 'prosemirror-tables';
import OrderedMap from 'orderedmap';

const XAMIX_ROOT =
  typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL != null
    ? (import.meta.env.BASE_URL || '/').replace(/\/$/, '') + '/node_modules/@tlenexkoyotl/xamix-element'
    : '/node_modules/@tlenexkoyotl/xamix-element';

export const XAMIX_ELEMENT_ROOT = XAMIX_ROOT;

const baseNodes = OrderedMap.from({
  text: { group: 'inline' },
  doc: { content: 'block+', toDOM: () => ['div', { class: 'manual-doc' }, 0] },
  paragraph: {
    content: 'inline*',
    group: 'block',
    parseDOM: [{ tag: 'p' }],
    toDOM: () => ['p', 0],
  },
  heading: {
    content: 'inline*',
    group: 'block',
    attrs: { level: { default: 1 } },
    parseDOM: [
      { tag: 'h1', attrs: { level: 1 } },
      { tag: 'h2', attrs: { level: 2 } },
      { tag: 'h3', attrs: { level: 3 } },
    ],
    toDOM: (node: { attrs: { level: number } }) => [`h${node.attrs.level}`, 0],
  },
  block_xamix: {
    content: '',
    group: 'block',
    atom: true,
    attrs: {
      textInput: { default: '' },
      level: { default: 0 }, // 0 = normal block, 1|2|3 = heading styling
      bold: { default: false },
      brush: { default: false },
      color: { default: null as string | null },
    },
    parseDOM: [{ tag: 'div[data-xamix]', getAttrs: (dom) => ({ textInput: (dom as HTMLElement).getAttribute('data-text-input') || '', level: parseInt((dom as HTMLElement).getAttribute('data-level') || '0', 10) || 0, bold: (dom as HTMLElement).getAttribute('data-bold') === 'true', brush: (dom as HTMLElement).getAttribute('data-brush') === 'true', color: (dom as HTMLElement).getAttribute('data-color') || null }) }],
    toDOM: (node: { attrs: { textInput: string; level?: number; bold?: boolean; brush?: boolean; color?: string | null } }) => {
      const attrs: Record<string, string> = { 'data-xamix': '', 'data-text-input': node.attrs.textInput, 'data-level': String(node.attrs.level ?? 0), 'data-bold': node.attrs.bold ? 'true' : 'false', 'data-brush': node.attrs.brush ? 'true' : 'false' };
      if (node.attrs.color) attrs['data-color'] = node.attrs.color;
      return ['div', attrs, 0];
    },
  },
  centered: {
    content: 'inline*',
    group: 'block',
    attrs: { size: { default: 'normal' } },
    parseDOM: [{ tag: 'div[data-centered]', getAttrs: (dom) => ({ size: (dom as HTMLElement).getAttribute('data-size') || 'normal' }) }],
    toDOM: (node: { attrs: { size: string } }) => ['div', { 'data-centered': '', 'data-size': node.attrs.size, class: `centered centered-${node.attrs.size}` }, 0],
  },
  page_break: {
    content: '',
    group: 'block',
    atom: true,
    parseDOM: [{ tag: 'div[data-page-break]' }],
    toDOM: () => ['div', { 'data-page-break': '', class: 'page-break-marker' }, 0],
  },
  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM: () => ['br'],
  },
  inline_image: {
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,
    attrs: {
      src: { default: '' },
      alt: { default: '' },
      title: { default: '' },
      width: { default: null as number | null },
      height: { default: null as number | null },
    },
    parseDOM: [{
      tag: 'img[data-inline-image]',
      getAttrs: (dom) => {
        const el = dom as HTMLImageElement;
        return {
          src: el.getAttribute('src') || '',
          alt: el.getAttribute('alt') || '',
          title: el.getAttribute('title') || '',
          width: el.hasAttribute('width') ? parseInt(el.getAttribute('width')!, 10) || null : null,
          height: el.hasAttribute('height') ? parseInt(el.getAttribute('height')!, 10) || null : null,
        };
      },
    }],
    toDOM: (node: { attrs: { src: string; alt: string; title: string; width: number | null; height: number | null } }) => {
      const attrs: Record<string, string> = { 'data-inline-image': '', src: node.attrs.src, alt: node.attrs.alt };
      if (node.attrs.title) attrs.title = node.attrs.title;
      if (node.attrs.width != null) attrs.width = String(node.attrs.width);
      if (node.attrs.height != null) attrs.height = String(node.attrs.height);
      return ['img', attrs];
    },
  },
  inline_svg: {
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,
    attrs: {
      content: { default: '' },
      width: { default: null as number | null },
      height: { default: null as number | null },
    },
    parseDOM: [{
      tag: 'span[data-inline-svg]',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        const svg = el.querySelector('svg');
        return {
          content: svg ? svg.outerHTML : el.getAttribute('data-svg-content') || '',
          width: el.hasAttribute('data-width') ? parseInt(el.getAttribute('data-width')!, 10) || null : null,
          height: el.hasAttribute('data-height') ? parseInt(el.getAttribute('data-height')!, 10) || null : null,
        };
      },
    }],
    toDOM: (node: { attrs: { content: string; width: number | null; height: number | null } }) => {
      const attrs: Record<string, string> = { 'data-inline-svg': '', 'data-svg-content': node.attrs.content };
      if (node.attrs.width != null) attrs['data-width'] = String(node.attrs.width);
      if (node.attrs.height != null) attrs['data-height'] = String(node.attrs.height);
      return ['span', attrs];
    },
  },
  xamix: {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: { textInput: { default: '' }, bold: { default: false }, brush: { default: false }, color: { default: null as string | null } },
    parseDOM: [{ tag: 'span[data-xamix-inline]', getAttrs: (dom) => ({ textInput: (dom as HTMLElement).getAttribute('data-text-input') || '', bold: (dom as HTMLElement).getAttribute('data-bold') === 'true', brush: (dom as HTMLElement).getAttribute('data-brush') === 'true', color: (dom as HTMLElement).getAttribute('data-color') || null }) }],
    toDOM: (node: { attrs: { textInput: string; bold?: boolean; brush?: boolean; color?: string | null } }) => {
      const attrs: Record<string, string> = { 'data-xamix-inline': '', 'data-text-input': node.attrs.textInput, 'data-bold': node.attrs.bold ? 'true' : 'false', 'data-brush': node.attrs.brush ? 'true' : 'false' };
      if (node.attrs.color) attrs['data-color'] = node.attrs.color;
      return ['span', attrs, 0];
    },
  },
});

const cellBackgroundColorAttr = {
  default: null as string | null,
  validate: (v: unknown) => {
    if (v != null && typeof v !== 'string') throw new TypeError('backgroundColor must be null or string');
  },
  getFromDOM(dom: Node) {
    const el = dom as HTMLElement;
    return el.getAttribute?.('data-cell-background') ?? (el as HTMLTableCellElement).style?.backgroundColor ?? null;
  },
  setDOMAttr(value: unknown, attrs: Record<string, string>) {
    if (value && typeof value === 'string') {
      attrs['data-cell-background'] = value;
      const style = (attrs.style ?? '') + (attrs.style ? ' ' : '') + `background-color: ${value}`;
      attrs.style = style;
    }
  },
};

const tableNodeSpecs = tableNodes({
  tableGroup: 'block',
  cellContent: 'block+',
  cellAttributes: { backgroundColor: cellBackgroundColorAttr },
});

const customTableSpec = {
  ...tableNodeSpecs.table,
  attrs: {
    ...(tableNodeSpecs.table.attrs || {}),
    margin: { default: '' },
    backgroundColor: { default: '' },
    borderColor: { default: '' },
    colWidths: { default: null },
  },
  toDOM(node: { attrs: { margin?: string; backgroundColor?: string; borderColor?: string; colWidths?: number[] | null } }) {
    const margin = node.attrs.margin ?? '';
    const bg = node.attrs.backgroundColor ?? '';
    const borderColor = node.attrs.borderColor ?? '';
    const colWidths = node.attrs.colWidths;
    const attrs: Record<string, string> = { class: 'manual-table' };
    let style = 'border-collapse: collapse; border: 1px solid ' + (borderColor || '#cac4d0') + '; table-layout: fixed;';
    if (margin) {
      style += `; margin: ${margin}`;
      attrs['data-margin'] = margin;
    }
    if (bg) {
      style += `; background-color: ${bg}`;
      attrs['data-table-background'] = bg;
    }
    if (borderColor) {
      style += `; --table-border-color: ${borderColor}`;
      attrs['data-table-border-color'] = borderColor;
    }
    attrs.style = style;
    
    // Generate colgroup with column widths if available
    const colCount = 3; // Default to 3 columns
    const cols: any[] = [];
    if (colWidths && Array.isArray(colWidths)) {
      for (const w of colWidths) {
        if (w != null && w > 0) {
          cols.push(['col', { style: `width: ${w}px` }]);
        } else {
          cols.push(['col', {}]);
        }
      }
    } else {
      for (let i = 0; i < colCount; i++) {
        cols.push(['col', {}]);
      }
    }
    
    return ['table', attrs, ['colgroup', {}, ...cols, ['tbody', 0]]];
  },
  parseDOM: [
    {
      tag: 'table',
      getAttrs(dom: Node | string) {
        const el = dom as HTMLElement;
        const tableEl = el as HTMLTableElement;
        return {
          margin: el.getAttribute?.('data-margin') ?? tableEl.style?.margin ?? '',
          backgroundColor: el.getAttribute?.('data-table-background') ?? tableEl.style?.backgroundColor ?? '',
          borderColor: el.getAttribute?.('data-table-border-color') ?? tableEl.style?.borderColor ?? '',
        };
      },
    },
  ],
};

const baseNodesWithTable = baseNodes.append(
  OrderedMap.from({ ...tableNodeSpecs, table: customTableSpec })
);
const nodes = addListNodes(baseNodesWithTable, 'paragraph block*', 'block');

export const manualSchema = new Schema({
  nodes,
  marks: {
    bold: {
      parseDOM: [{ tag: 'strong' }, { tag: 'b', getAttrs: () => ({}) }, { style: 'font-weight', getAttrs: (v) => /^(bold(er)?|[5-9]\d{2,})$/.test(v as string) && {} }],
      toDOM: () => ['strong', 0],
    },
    italic: {
      parseDOM: [{ tag: 'i' }, { tag: 'em' }],
      toDOM: () => ['em', 0],
    },
    textColor: {
      attrs: { color: { default: '#000000' } },
      parseDOM: [{ style: 'color', getAttrs: (v) => (v ? { color: v as string } : false) }],
      toDOM: (mark) => ['span', { style: `color: ${mark.attrs.color}` }, 0],
    },
    highlight: {
      attrs: {
        color: { default: '#fef08a' },
        textColor: { default: null as string | null },
      },
      parseDOM: [
        { style: 'background-color', getAttrs: (v) => (v ? { color: v as string } : false) },
        // Also parse mark elements with both color and textColor
        { tag: 'mark', getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return {
            color: el.style.backgroundColor || '#fef08a',
            textColor: el.style.color || null,
          };
        }},
      ],
      toDOM: (mark) => {
        const style = `background-color: ${mark.attrs.color}`;
        const textColorStyle = mark.attrs.textColor ? `; color: ${mark.attrs.textColor}` : '';
        return ['mark', { style: style + textColorStyle }, 0];
      },
    },
  },
});
